import type { CreateParams, GetListParams, GetOneParams, UpdateParams } from "react-admin";
import { getAuthenticatedBackendClient, reactAdminToDRFPagination, reactAdminToDRFOrdering } from "../api/backend.api";
import type { DATEVConfig, DATEVExportRun, PatchedDATEVConfig } from "../generated/backendClient";
import { AbstractDataProvider } from "./AbstractDataProvider";

export type DATEVConfigRecord = DATEVConfig & { id: string; effective_on?: string };
export type DATEVExportRunRecord = DATEVExportRun;

function configRecord(config: DATEVConfig): DATEVConfigRecord {
  if (!config.id) throw new Error("DATEV-Konfiguration ohne ID");
  return { ...config, id: config.id };
}

class DATEVConfigsProvider extends AbstractDataProvider<DATEVConfigRecord> {
  async getList(resource: string, params: GetListParams) {
    const client = await getAuthenticatedBackendClient();
    if (!params.filter.drivingSchoolId) throw new Error("Fahrschule fehlt");
    const response = await client.datev.datevConfigsList({
      companyDrivingSchoolId: params.filter.drivingSchoolId,
      ...reactAdminToDRFPagination(params.pagination),
      ordering: reactAdminToDRFOrdering(params.sort, false),
    });
    return { data: response.results.map(configRecord), total: response.count };
  }
  async getOne(resource: string, params: GetOneParams) {
    const client = await getAuthenticatedBackendClient();
    return { data: configRecord(await client.datev.datevConfigsRetrieve({ id: String(params.id) })) };
  }
  async create(resource: string, params: CreateParams<DATEVConfigRecord>) {
    const client = await getAuthenticatedBackendClient();
    if (!params.data.id || !params.data.effective_on) throw new Error("Konfiguration oder Gültigkeitsbeginn fehlt");
    return {
      data: configRecord(
        await client.datev.datevConfigsVersionCreate({
          id: params.data.id,
          requestBody: { ...params.data, effective_on: params.data.effective_on },
        }),
      ),
    };
  }
  async update(resource: string, params: UpdateParams<DATEVConfigRecord>) {
    const client = await getAuthenticatedBackendClient();
    const fields: PatchedDATEVConfig = params.data;
    return {
      data: configRecord(await client.datev.datevConfigsPartialUpdate({ id: String(params.id), requestBody: fields })),
    };
  }
}

class DATEVExportRunsProvider extends AbstractDataProvider<DATEVExportRunRecord> {
  async getList(resource: string, params: GetListParams) {
    const client = await getAuthenticatedBackendClient();
    if (!params.filter.drivingSchoolId) throw new Error("Fahrschule fehlt");
    const response = await client.datev.datevExportRunsList({
      companyDrivingSchoolId: params.filter.drivingSchoolId,
      mode: params.filter.mode,
      status: params.filter.status,
      isFaulty: params.filter.is_faulty,
      customerDownloaded: params.filter.customer_downloaded,
      ...reactAdminToDRFPagination(params.pagination),
      ordering: reactAdminToDRFOrdering(params.sort, false),
    });
    return { data: response.results, total: response.count };
  }
  async getOne(resource: string, params: GetOneParams) {
    const client = await getAuthenticatedBackendClient();
    return { data: await client.datev.datevExportRunsRetrieve({ id: String(params.id) }) };
  }
}
export const datevConfigsProvider = new DATEVConfigsProvider();
export const datevExportRunsProvider = new DATEVExportRunsProvider();
